/** @format */
/* eslint-disable no-case-declarations */

import { isPlatformBrowser } from '@angular/common';
import {
	Component,
	ComponentRef,
	Inject,
	Injector,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	ViewChild,
	ViewContainerRef,
	ɵcreateInjector as createInjector,
	AfterViewInit
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Observable, Subscription, zip } from 'rxjs';
import { catchError, pluck } from 'rxjs/operators';

import {
	CookieService,
	EmitEvent,
	EventBusService,
	Events,
	Logger,
	triggerTargetView
} from '@shared/data-access/services';

import {
	CategoryAnalyticsService,
	CategorySEOData,
	CategorySeoUrlToken,
	DynamicCategoryTemplateModules
} from '@category/data-access';

import { HomrDataService, HomrPage } from '@shared/homr/data-access';
import { SeoService } from '@seo/data-access';

@Component({
	selector: 'clp-category-container',
	templateUrl: './category-container.component.html',
	styleUrl: './category-container.component.scss',
	standalone: false
})
export class CategoryContainerComponent implements OnInit, AfterViewInit, OnDestroy {
	/**
	 * @type {OperatorFunction<T, R>}
	 *
	 * @description
	 * Category Observable to be displayed in the view
	 */
	category$: Observable<CategorySeoUrlToken> = this.route.data.pipe(pluck('category'));

	/**
	 * @description
	 * The Object for displaying category Data from Category API response
	 */
	categoryData: CategorySEOData;

	/**
	 * @description
	 * The seoToken of category page
	 *
	 * @type {string}
	 */
	seoToken: string;

	private categorySubscription: Subscription;

	/**
	 * @description
	 * Template outlet for dynamically loaded templates
	 */
	@ViewChild('templateOutlet', { read: ViewContainerRef, static: false })
	templateOutlet: ViewContainerRef;

	templateData: HomrPage;
	isLoaded = false;
	/**
	 * @description
	 * Flag for displaying the Third Party content
	 */
	isMainContentLoaded = false;

	constructor(
		@Inject(PLATFORM_ID) protected platformId: any,
		public route: ActivatedRoute,
		private homrDataService: HomrDataService,
		private analyticsService: CategoryAnalyticsService,
		private injector: Injector,
		private cookieService: CookieService,
		private logger: Logger,
		private seoService: SeoService,
		private eventBusService: EventBusService
	) {}
	ngOnInit() {
		const promoId = this.route.snapshot.queryParams['param'];
		if (promoId && !this.cookieService.check('promoId')) {
			this.cookieService.set('promoId', promoId, '', '/');
		}

		const homrObservable = this.homrDataService.getData$(`category`, 2).pipe(
			catchError((error) => {
				this.logger.error('Error getting homr data in category container', error);

				return this.homrDataService.getDefault$(`category`);
			})
		);

		this.categorySubscription = zip(this.category$, homrObservable).subscribe((result) => {
			this.handleForkedSubscription({ category: result[0], homrData: result[1] });
		});
	}

	ngAfterViewInit(): void {
		this.displayThirdPartyAndEmitEnableFooter();
	}

	handleForkedSubscription(result: any) {
		this.handleCategorySubscription(result.category);
		this.templateData = result.homrData;
		if (
			this.templateData &&
			this.templateData.templateName &&
			this.templateData.content &&
			this.categoryData
		) {
			this.loadDynamicModule(this.templateData.templateName);
			triggerTargetView('category--post-content-load');
		} else {
			this.logger.debug('Unable to load template', this.templateData, this.categoryData);
		}
	}

	handleCategorySubscription(category: any) {
		if (category !== null && category.categories[0]) {
			this.categoryData = category.categories[0];

			// Get the seoToken
			this.seoToken = this.categoryData.seoUrl;

			if (this.categoryData.childVOs) {
				this.categoryData.childVOs.forEach((childCat) => {
					childCat.expanded = false;
					childCat.url = `/${childCat.pageType}/${childCat.seoUrl}`;
					childCat.fullImage = this.seoService.getimagePath(childCat);
					const categoryName = childCat.note ? childCat.note : childCat.name;
					childCat.em = `LN_${categoryName}`;
				});

				// Remove ChildVOs not having Image
				this.categoryData.childVOs = this.categoryData.childVOs.filter((subcat) => {
					return subcat.fullImage !== '';
				});
			}

			if (isPlatformBrowser(this.platformId)) {
				this.analyticsService.setCategoryID(this.categoryData.id);
				this.analyticsService.setCategoryName(this.categoryData.name);

				// Set the search redirect Analytics
				const sTerm = this.cookieService.get('sTermCookie');
				if (sTerm) {
					this.analyticsService.setEnteredSearchTerm(sTerm);
					this.analyticsService.setSearchType('Redirect');
				}

				// Trigger the Post-render Event
				this.analyticsService.triggerAnalyticsEvent('NewPageView');
			}
		}
	}

	ngOnDestroy() {
		if (this.categorySubscription) {
			this.categorySubscription.unsubscribe();
		}
	}

	/**
	 * @description
	 * Loads a dynamic module by name
	 */
	async loadDynamicModule(componentName: string = DynamicCategoryTemplateModules.CHECK_BACK) {
		switch (componentName) {
			/* istanbul ignore next */
			case DynamicCategoryTemplateModules.ONE_SPOT: {
				const { TemplateOneSpotModule } = await import('@category/templates');
				this.createDynamicComponent(TemplateOneSpotModule);
				break;
			}
			/* istanbul ignore next */
			case DynamicCategoryTemplateModules.THREE_SPOT: {
				const { TemplateThreeSpotModule } = await import('@category/templates');
				this.createDynamicComponent(TemplateThreeSpotModule);
				break;
			}
			/* istanbul ignore next */
			case DynamicCategoryTemplateModules.ONE_SPOT_WITH_DEALS:
			case DynamicCategoryTemplateModules.THREE_SPOT_WITH_DEALS: {
				const { TemplateSpotWithDealsModule } = await import('@category/templates');
				this.createDynamicComponent(TemplateSpotWithDealsModule);
				break;
			}
			/* istanbul ignore next */
			case DynamicCategoryTemplateModules.CHECK_BACK: {
				const { TemplateCheckBackModule } = await import('@category/templates');
				this.createDynamicComponent(TemplateCheckBackModule);
				break;
			}
			default:
				throw new Error(
					`Unknown component Name: ${componentName}.  Unable to render any lazy components.`
				);
		}
	}

	/* istanbul ignore next */
	/**
	 * @description
	 * Creates a dynamic component for a given component module
	 */
	private createDynamicComponent(componentModuleRef: any) {
		// Create the injector
		const injector = createInjector(componentModuleRef, this.injector);

		// Get the component module from the injector
		const componentModule = injector.get<any>(componentModuleRef);

		// Resolve the component factory
		const componentFactory = componentModule.resolveComponentFactory();

		// Create the template component and get its reference
		const componentRef: ComponentRef<any> =
			this.templateOutlet.createComponent(componentFactory);

		// Set the categoryData on the template reference
		componentRef.instance['categoryData'] = this.categoryData;
		componentRef.instance['data'] = this.templateData;
		componentRef.instance['pageType'] = 'category';

		// Start change detection
		componentRef.changeDetectorRef.detectChanges();
		this.isLoaded = true;
	}

	private displayThirdPartyAndEmitEnableFooter(): void {
		this.isMainContentLoaded = true;
		this.eventBusService.emit(new EmitEvent(Events.EnableFooter));
	}
}
